import { useQuery } from '@tanstack/react-query';
import { CacheKey } from '../types';
import { accountingClient } from '../utils/request';
import { useDebounce } from './useDebounce';

export interface VatNumberValidator {
  isVatLoading: boolean;
  isVatValid: boolean;
}

export const useVatNumberValidator = (inputNumber: string): VatNumberValidator => {
  const vatNumber = useDebounce(inputNumber, 250);
  const enabled = vatNumber.length > 8;
  const { isPending, data } = useQuery({
    queryKey: [CacheKey.VAT_NUMBER_VALIDATOR, vatNumber],
    queryFn: async () => {
      const isValid = await accountingClient.vat.isVatNumberValid({ vatNumber });
      // Calling VIES directly returned sometimes valid: true and sometimes MS_MAX_CONCURRENT_REQ.
      // This means the concurrent request limit for the Netherlands was reached.
      // But is returned as valid: false. So we throw an error to retry the request.
      if (!isValid) throw new Error('VAT could be not valid');
      return isValid;
    },
    retry: (failureCount, error) => error.message.includes('VAT could be not valid') && failureCount < 5,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30_000),
    enabled: enabled,
  });
  if (!enabled) return { isVatLoading: false, isVatValid: false };
  /**
   * Loading
   */
  if (isPending) return { isVatLoading: true, isVatValid: false };

  /**
   * Success
   */
  if (data === true) return { isVatLoading: false, isVatValid: true };

  /**
   * Failed
   */
  return { isVatLoading: false, isVatValid: false };
};
